
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Trophy, Star, Crown, ArrowRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import { useLeaderboardData } from "@/hooks/useLeaderboardData";

const ContributorSection = () => {
  const [timeFrame] = useState<"all-time" | "monthly" | "weekly">("all-time");
  const { leaderboard, isLoading } = useLeaderboardData(timeFrame);
  
  // Get only top 3 contributors
  const topContributors = leaderboard.slice(0, 3);
  
  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4 flex items-center justify-center gap-3">
            <Trophy className="h-8 w-8 text-yellow-500" />
            <span>Top Prompt Contributors</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Recognizing our community members who create and share the most valuable prompts.
            Join them on the leaderboard!
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-10">
          {isLoading ? (
            Array(3).fill(0).map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-lg p-6 flex flex-col items-center animate-pulse">
                <div className="w-20 h-20 bg-muted rounded-full mb-4"></div>
                <div className="h-6 bg-muted rounded w-24 mb-3"></div>
                <div className="h-4 bg-muted rounded w-16 mb-4"></div>
                <div className="flex gap-2 mb-4">
                  <div className="h-6 w-24 bg-muted rounded"></div>
                </div>
                <div className="h-10 bg-muted rounded w-full"></div>
              </div>
            ))
          ) : topContributors.length > 0 ? (
            topContributors.map((contributor, index) => (
              <div 
                key={contributor.id} 
                className="bg-background border border-border rounded-lg p-6 flex flex-col items-center relative"
              >
                {index === 0 && (
                  <div className="absolute -top-3 -right-3">
                    <Crown className="h-8 w-8 text-yellow-500" />
                  </div>
                )}
                
                <div className="mb-2 text-center">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
                    <span className="font-bold text-primary text-lg">#{index + 1}</span>
                  </div>
                </div>
                
                <Avatar className="w-20 h-20 mb-3">
                  <AvatarFallback className="text-xl">
                    {contributor.userName ? contributor.userName[0].toUpperCase() : '?'}
                  </AvatarFallback>
                  {contributor.avatar && <AvatarImage src={contributor.avatar} />}
                </Avatar>
                
                <h3 className="text-xl font-bold mb-1">{contributor.userName || 'Anonymous'}</h3>
                <div className="flex items-center gap-1 text-muted-foreground mb-3">
                  <Star className="h-4 w-4 text-yellow-500" />
                  <span>Level {contributor.level}</span>
                </div>
                
                <div className="flex flex-wrap justify-center gap-1 mb-4">
                  <Badge variant="outline">{contributor.points.toLocaleString()} points</Badge>
                </div>
                
                <Link 
                  to={`/profile/${contributor.id}`}
                  className="text-center text-sm text-primary font-medium hover:underline"
                >
                  View profile
                </Link>
              </div>
            ))
          ) : (
            Array(3).fill(0).map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-lg p-6 text-center">
                <p className="text-muted-foreground">No contributors found</p>
              </div>
            ))
          )}
        </div>
        
        <div className="text-center">
          <Button asChild>
            <Link to="/leaderboard" className="flex items-center gap-2">
              View Full Leaderboard
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ContributorSection;
